import {
  META_PIXEL_ID,
  TIKTOK_PIXEL_ID,
  LINKEDIN_PARTNER_ID,
} from "../config/tracking";
import { isPrerenderEnv } from "./googleAnalytics";
import {
  clearMarketingCookies,
  startCookieSweep,
  stopCookieSweep,
} from "./clearAnalyticsCookies";

const META_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const LINKEDIN_SCRIPT_SRC =
  "https://snap.licdn.com/li.lms-analytics/insight.min.js";
const TIKTOK_SCRIPT_SRC = () =>
  `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${TIKTOK_PIXEL_ID}&lib=ttq`;

const PIXEL_SRC_MARKERS = [
  "connect.facebook.net",
  "facebook.com/tr",
  "facebook.net/signals",
  "analytics.tiktok.com",
  "ads.tiktok.com",
  "snap.licdn.com",
  "px.ads.linkedin.com",
];

const appendScriptOnce = (src) => {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const srcMatchesPixel = (src) =>
  PIXEL_SRC_MARKERS.some((marker) => src.includes(marker));

const removeMarketingScripts = () => {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll("script[src], iframe[src], img[src]")
    .forEach((el) => {
      const src = el.getAttribute("src") || "";
      if (srcMatchesPixel(src)) {
        el.remove();
      }
    });
};

const createFbqStub = () => {
  const fbq = function fbq() {
    fbq.queue.push(arguments);
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.push = fbq;
  return fbq;
};

const loadMetaPixel = () => {
  if (!META_PIXEL_ID || window.__fbPixelLoaded) return;

  if (!window.fbq) {
    window.fbq = createFbqStub();
    window._fbq = window.fbq;
  }

  appendScriptOnce(META_SCRIPT_SRC);
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
  window.__fbPixelLoaded = true;
};

const loadTikTokPixel = () => {
  if (!TIKTOK_PIXEL_ID || window.__ttqLoaded) return;

  const ttq = window.ttq || [];
  window.TiktokAnalyticsObject = "ttq";
  window.ttq = ttq;
  ttq.methods = [
    "page",
    "track",
    "identify",
    "instances",
    "debug",
    "on",
    "off",
    "once",
    "ready",
    "alias",
    "group",
    "enableCookie",
    "disableCookie",
    "holdConsent",
    "revokeConsent",
    "grantConsent",
  ];
  ttq.setAndDefer = function setAndDefer(obj, method) {
    obj[method] = function deferredTikTokCall() {
      obj.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  ttq.methods.forEach((method) => ttq.setAndDefer(ttq, method));

  appendScriptOnce(TIKTOK_SCRIPT_SRC());

  if (typeof ttq.load === "function") {
    ttq.load(TIKTOK_PIXEL_ID);
  } else {
    ttq.push(["load", TIKTOK_PIXEL_ID]);
  }
  if (typeof ttq.page === "function") {
    ttq.page();
  } else {
    ttq.push(["page"]);
  }
  window.__ttqLoaded = true;
};

const loadLinkedInPixel = () => {
  if (!LINKEDIN_PARTNER_ID || window.__liPixelLoaded) return;

  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(LINKEDIN_PARTNER_ID)) {
    window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
  }

  if (!window.lintrk) {
    window.lintrk = function lintrk(a, b) {
      window.lintrk.q.push([a, b]);
    };
    window.lintrk.q = [];
  }

  appendScriptOnce(LINKEDIN_SCRIPT_SRC);
  window.__liPixelLoaded = true;
};

export const loadMarketingPixels = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (isPrerenderEnv()) return;
  if (window.__marketingLoaded) return;

  stopCookieSweep("marketing");
  loadMetaPixel();
  loadTikTokPixel();
  loadLinkedInPixel();
  window.__marketingLoaded = true;
};

export const trackMarketingPageView = () => {
  if (typeof window === "undefined" || !window.__marketingLoaded) return;

  if (typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
  if (window.ttq && typeof window.ttq.page === "function") {
    window.ttq.page();
  }
  if (typeof window.lintrk === "function") {
    window.lintrk("track");
  }
};

export const disableMarketingPixels = () => {
  if (typeof window === "undefined") return;

  if (window.fbq && typeof window.fbq === "function") {
    try {
      window.fbq("consent", "revoke");
    } catch (e) {
      // fbq stub may not support consent
    }
  }
  if (window.ttq && typeof window.ttq.revokeConsent === "function") {
    window.ttq.revokeConsent();
  }

  removeMarketingScripts();
  delete window.fbq;
  delete window._fbq;
  delete window.ttq;
  delete window.TiktokAnalyticsObject;
  delete window.lintrk;
  delete window._linkedin_partner_id;
  window._linkedin_data_partner_ids = [];

  window.__marketingLoaded = false;
  window.__fbPixelLoaded = false;
  window.__ttqLoaded = false;
  window.__liPixelLoaded = false;
  startCookieSweep("marketing", clearMarketingCookies);
};
